import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCheck } from 'lucide-react'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/components/forms/Form'
import { Input } from '@/ui/components/forms/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { TaxonomyTermPicker } from '@/ui/components/forms/TaxonomyTermPicker'
import { ImageField } from '@/ui/components/forms/ImageField'
import { useCreateInvestigation } from '@/application/services/investigation.service'
import { useDeleteSuggestion } from '@/application/services/suggestion.service'
import { DEFAULT_CONTENT_LOCALE } from '@/application/i18n/language'
import type { Suggestion, TaxonomyTerm } from '@/domains/types'

const makeApproveSchema = (translate: (k: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    name: z
      .string()
      .min(2, translate('validation.name_min'))
      .max(250, translate('validation.name_max')),
    description: z.string().max(20000, translate('validation.desc_max')),
    investigationId: z
      .string()
      .min(2, translate('validation.id_min'))
      .max(200, translate('validation.id_max')),
    imageUrl: z
      .string()
      .max(2048)
      .refine(
        (value) =>
          !value ||
          value.startsWith('/') ||
          (() => {
            try {
              new URL(value)
              return true
            } catch {
              return false
            }
          })(),
        { message: translate('validation.image_url') },
      )
      .optional()
      .or(z.literal('')),
  })

type ApproveFormValues = z.infer<ReturnType<typeof makeApproveSchema>>

interface ApproveDialogProps {
  suggestion: Suggestion
  open: boolean
  onClose: () => void
}

/**
 * Modal dialog that lets a privileged user turn a viewer-submitted
 * suggestion into an actual investigation: pre-fills the create form from
 * the suggestion, creates the investigation, deletes the suggestion, then
 * navigates back to the investigations list.
 */
export function ApproveDialog({ suggestion, open, onClose }: ApproveDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: createInvestigation, isPending } = useCreateInvestigation()
  const { mutateAsync: deleteSuggestion } = useDeleteSuggestion()
  // A suggestion carries no source language: the backend assumes the default one.
  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])
  const schema = useMemo(() => makeApproveSchema(t), [t])

  const form = useForm<ApproveFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '', investigationId: '', imageUrl: '' },
  })

  useEffect(() => {
    if (open && suggestion) {
      const slug = suggestion.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 200)
      form.reset({
        name: suggestion.title,
        description: suggestion.description ?? '',
        investigationId: slug,
        imageUrl: '',
      })
      setCategoryTerms([])
    }
  }, [open, suggestion, form])

  const handleSubmit = async (values: ApproveFormValues) => {
    try {
      const result = await createInvestigation({
        ...values,
        categoryTermIds: categoryTerms.map((term) => term.id),
        suggestedByUsername: suggestion.suggestedBy,
      })
      try {
        await deleteSuggestion(suggestion.suggestionId)
      } catch {
        /* ignore — investigation was created */
      }
      toast.success(t('admin.suggestions_approved'))
      onClose()
      navigate({
        to: '/manage/investigations',
        search: { statusFilter: result.status ?? 'pending' },
      })
    } catch {
      toast.error(t('admin.suggestions_approve_failed'))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(prev) => {
        if (!prev) onClose()
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('admin.suggestions_approve_dialog_title')}</DialogTitle>
          <DialogDescription>{t('admin.suggestions_approve_dialog_subtitle')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5 mt-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_name_label')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.form_name_placeholder')} {...field} />
                  </FormControl>
                  <FormDescription>{t('admin.form_name_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_desc_label')}</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('admin.form_desc_placeholder')}
                      disabled={isPending}
                      onUploadError={(message) => toast.error(message)}
                    />
                  </FormControl>
                  <FormDescription>{t('admin.form_desc_helper')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="investigationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_id_label')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.form_id_placeholder')} {...field} />
                  </FormControl>
                  <FormDescription>{t('admin.form_id_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium leading-none">
                  {t('admin.form_category_label')}
                </label>
              </div>
              <TaxonomyTermPicker
                locale={DEFAULT_CONTENT_LOCALE}
                value={categoryTerms}
                onChange={setCategoryTerms}
                placeholder={t('admin.form_category_placeholder')}
                disabled={isPending}
              />
              <p className="text-sm text-muted-foreground">{t('admin.form_category_desc')}</p>
            </div>
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.form_image_label')}</FormLabel>
                  <FormControl>
                    <ImageField
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>{t('admin.form_image_desc')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('admin.form_cancel')}
              </Button>
              <Button
                type="submit"
                isLoading={isPending}
                leftIcon={<CheckCheck className="w-4 h-4" />}
              >
                {t('admin.form_create_submit')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
